import { useActualIncidentId, useGroupGuid, useUserGuid } from './commonHooks';
import { useIncidentScopeId } from './incidentScope';
import { selectIncident, useAppSelector } from 'slices/commonSelectors';
import {
  useQuery,
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from 'constants/apiConfig';
import dayjs from 'dayjs';
import parseErrorMessage from 'utils/parseErrorMessage';
import { applyExerciseIncidentName } from 'utils/incidentExerciseName';
import { noAutoClose } from 'assets/data/config';
import { validate as validateUUID } from 'uuid';
import {
  isInitialResponsePeriod,
  resolveWorkingPeriodNumber,
} from 'utils/workingOperationalPeriod';
import type { Geometry } from 'geojson';

export type Incident = {
  id: UUID;
  name: string;
  archived: number;
  settings?: Record<string, any>;
  current_period?: UUID;
  operational_period_interval?: number;
  incident_created?: string;
  /**
   * Set when the incident is escalated into Planning P. Distinct from the
   * operational period number: an escalated incident stays on period 0 for its
   * whole first planning cycle, so this is the only reliable "is escalated"
   * signal.
   */
  escalated_at?: string | null;

  active?: boolean;

  severity?: 'Low' | 'Medium' | 'High';
  type?: string; // Medical, Fire, Earthquake, etc
  incident_types: string[]; // [Medical, Fire, Earthquake], etc
  areas_of_responsibility: string[];
  /** Org event ids linked via incident_org_events (when present from API). */
  related_event_ids?: string[];

  teams_id?: UUID;
  sharepoint_location?: string;
  channel_id?: string;
  tenant_id?: UUID;

  status: 'unstarted' | 'inprogress' | 'done';

  location?: Geometry | null;
  /** Geocoded address captured when the incident was created. */
  address?: string | null;
};

export const keys = {
  base: 'incident',
  get: (incidentId: UUID) => [keys.base, incidentId],
  all: (group_guid: UUID) => [keys.base, 'all', group_guid],
  periods: (incidentId: UUID) => [keys.base, 'periods', incidentId],
};

/**
 * Query options for fetching a single incident - usable with
 * queryClient.fetchQuery()
 */
export const incidentQueryOptions = (incidentId: string) => ({
  queryKey: keys.get(incidentId),
  queryFn: async () => {
    const resp = await axios.get<Incident>(
      `${config.apiGateway.ics_incident}/${incidentId}`
    );
    return resp.data;
  },
});

/**
 * Query options for fetching operational periods - usable with
 * queryClient.fetchQuery()
 */
export const periodsQueryOptions = ({
  incidentId,
  enabled,
}: {
  incidentId: string;
  enabled?: boolean;
}) => ({
  queryKey: keys.periods(incidentId),
  queryFn: async () => {
    const resp = await axios.get<OperationalPeriod[]>(
      `${config.apiGateway.ics_periods}/${incidentId}`
    );
    return resp.data;
  },
  enabled,
});

export const invalidateIncidentQueries = (
  queryClient: QueryClient,
  groupId: string
) => {
  return queryClient.invalidateQueries({
    queryKey: keys.all(groupId),
  });
};

export const useExactIncident = (incidentId: string) => {
  return useQuery({
    ...incidentQueryOptions(incidentId),
    enabled: !!incidentId && validateUUID(incidentId),
  });
};

export const useIncident = () => {
  const incidentId = useActualIncidentId();
  return useExactIncident(incidentId);
};

/**
 * The workspace incident record cards read for its name, family, and period:
 * the scoped incident when inside an `IncidentScopeProvider`, otherwise the
 * open workspace from Redux.
 */
export const useScopedIncident = (): IncidentAugmented | undefined => {
  const scopedIncidentId = useIncidentScopeId();
  const openIncident = useAppSelector(selectIncident);
  const { data: scopedIncident } = useExactIncident(scopedIncidentId ?? '');
  if (!scopedIncidentId) return openIncident ?? undefined;
  return scopedIncident as IncidentAugmented | undefined;
};

export const useAllIncidents = () => {
  const group_guid = useGroupGuid();
  return useQuery({
    queryKey: keys.all(group_guid),
    queryFn: async () => {
      const resp = await axios.get<Incident[]>(
        `${config.apiGateway.ics_incidents}/${group_guid}`
      );
      return resp.data;
    },
  });
};

export const useCreateIncident = () => {
  const groupGuid = useGroupGuid();
  const userGuid = useUserGuid();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      incident: Pick<
        Incident,
        'name' | 'areas_of_responsibility' | 'incident_types'
      > & {
        group_guid: UUID;
        activated_users: any[];
        /** Which authority's ICS forms this incident is authored against. */
        ics_form_family?: string;
        /**
         * Which channels carry the activation requests. Omitted means the
         * backend default of email, SMS and voice.
         */
        activation_channels?: string[];
        /** Extra copy appended to each activation request. */
        activation_message?: string;
        /** Load-template slug. Omit to keep the full standard ICS tree. */
        roster_template_slug?: string | null;
        /** Edited draft org chart. When set, seeds exactly these seats. */
        roster_nodes?: {
          name: string;
          parent: string | null;
          isWorkgroup?: boolean;
        }[];
        /** Contact-list people from a filled roster who have no user account. */
        hwcg_roster_externals?: {
          seatName: string;
          name: string;
          email: string;
          phone: string;
        }[];
        /** HWCG Group Actions. The server contacts the matching contact lists. */
        hwcg_group_actions?: Record<string, string | null>;
        /** Company named as the responsible party for this incident. */
        hwcg_responsible_party?: string;
        /** EOC notice composed in the create-incident wizard. */
        hwcg_eoc?: {
          action: 'activate' | 'notify';
          recipientContactIds: string[];
          confidentialityScript: string;
          noticeScript: string;
          channels: string[];
        };
        hwcg_group_notices?: Record<
          string,
          {
            action: 'activate' | 'notify';
            recipientContactIds: string[];
            confidentialityScript: string;
            noticeScript: string;
            channels: string[];
          }
        >;
        hwcg_authorization?: {
          contactId: string;
          date: string;
          time: string;
          caller: string;
          description: string;
          confirmed: boolean;
        };
        /**
         * Operational period windows. Period 1 can start when the incident is
         * created; the rest become planned periods.
         */
        operational_schedule?: {
          startFirstOnCreate: boolean;
          periods: { start: string | null; end: string | null }[];
        };
      }
    ) => {
      const name = applyExerciseIncidentName(
        incident.name,
        incident.incident_types
      );
      const req = JSON.stringify({
        user_guid: userGuid,
        ...incident,
        name,
      });
      const response = await axios.post<Incident>(
        `${config.apiGateway.createIncident}`,
        req
      );
      return response.data;
    },
    onError: (error: string) => {
      const errorMessage =
        'Error Creating Incident:\n\n' +
        parseErrorMessage(error) +
        '\n\nIf you continue to experience this error, contact support@disastertech.com';
      console.error(errorMessage);
      toast.error(errorMessage, noAutoClose);
    },
    onSuccess: () => {
      invalidateIncidentQueries(queryClient, groupGuid);
    },
  });
};

export type OperationalPeriod = {
  anticipated_end_date_time?: string;
  end_date_time: string;
  id: UUID;
  incident_id: UUID;
  period_number?: number;
  start_date_time: string;
  phase_number_completed?: number;
};

export const usePeriods = (enabled?: boolean) => {
  const incidentId = useActualIncidentId();
  const hasValidIncidentId = !!incidentId && validateUUID(incidentId);
  const isEnabled = (enabled ?? true) && hasValidIncidentId;

  return useQuery(
    periodsQueryOptions({
      incidentId,
      enabled: isEnabled,
    })
  );
};

export const useCurrentPeriod = () => {
  const incident = useIncident();
  const periods = usePeriods();

  // Since we're just calculating an object from here, return empty object until we have data
  if (
    periods.isLoading ||
    incident.isLoading ||
    !incident.isSuccess ||
    !periods.isSuccess
  ) {
    return {
      // This is wrong but we have to do it for now. Too many consumers can't handle undefined.
      periodNumber: 0,
    };
  }

  const currentPeriod = periods.data.find(
    (p) => p.id === incident.data.current_period
  );

  if (!currentPeriod) {
    return {
      periodNumber: 0,
      startDate: dayjs(incident.data.incident_created),
    };
  } else {
    return {
      currentPeriod,
      periodNumber: currentPeriod.period_number ?? 0,
      startDate: dayjs(currentPeriod.start_date_time),
      anticipatedEnd: dayjs(currentPeriod.anticipated_end_date_time),
    };
  }
};

/**
 * Whether the incident has been escalated into the Planning P workflow.
 *
 * Do not infer this from the operational period number. Escalation no longer
 * advances the period, so an escalated incident sits on period 0 until the
 * first Planning P cycle completes and the Operations Briefing creates period
 * 1.
 */
export const useIsEscalated = () => {
  const incident = useIncident();
  return !!incident.data?.escalated_at;
};

/**
 * Whether the incident is in Initial Response: created, not yet escalated.
 *
 * The one predicate that decides which workspace an incident gets and which
 * operational period its forms are authored against, so the dashboard, the
 * Initial Response page and `ICSContext` all read it from here rather than each
 * spelling it out. Period 0 alone is not enough: an escalated incident stays on
 * period 0 for its whole first Planning P cycle and belongs to Planning P
 * throughout.
 */
export const useIsInitialResponse = () => {
  const { periodNumber } = useCurrentPeriod();
  const isEscalated = useIsEscalated();
  return isInitialResponsePeriod({
    currentPeriodNumber: periodNumber ?? 0,
    isEscalated,
  });
};

/**
 * The operational period forms are authored against: 0 during Initial Response,
 * otherwise the next one.
 *
 * Sits beside {@link useIsInitialResponse} because both answer the same question
 * from the same two inputs, and having one of them spelled out inline at its
 * call site is how they drift apart.
 */
export const useWorkingPeriodNumber = () => {
  const { periodNumber } = useCurrentPeriod();
  const isEscalated = useIsEscalated();
  return resolveWorkingPeriodNumber({
    currentPeriodNumber: periodNumber ?? 0,
    isEscalated,
  });
};

/**
 * Shown in place of a period number while an escalated incident is still
 * preparing its first operational period.
 */
export const NO_OPERATIONAL_PERIOD_LABEL = 'None';

/**
 * Labels for the Current/Future period toggle.
 *
 * Between escalation and the first Operations Briefing no operational period is
 * underway yet: the incident sits on period 0 while the Planning P cycle
 * prepares period 1. That reads as "None" rather than "0", and the future
 * period is 1 — which the usual periodNumber + 1 already yields.
 */
export const useOperationalPeriodLabels = () => {
  const { periodNumber } = useCurrentPeriod();
  const isEscalated = useIsEscalated();
  const incident = useIncident();
  const periods = usePeriods();

  // useCurrentPeriod reports period 0 while loading, which would otherwise flash "None" on an incident that is really
  // on period 3.
  const isReady = incident.isSuccess && periods.isSuccess;
  const hasCurrentPeriod = periodNumber > 0;

  return {
    periodNumber,
    hasCurrentPeriod,
    /** Whether the Current/Future toggle applies at all. */
    showPeriodToggle: isReady && (hasCurrentPeriod || isEscalated),
    currentPeriodLabel: hasCurrentPeriod
      ? String(periodNumber)
      : NO_OPERATIONAL_PERIOD_LABEL,
    futurePeriodNumber: periodNumber + 1,
  };
};

export const useIncidentPermissions = () => {
  const incidentId = useActualIncidentId();
  const groupGuid = useGroupGuid();

  return useQuery({
    queryKey: ['incidentPermissions', groupGuid, incidentId],
    queryFn: async () => {
      const resp = await axios.post<Record<string, boolean>>(
        config.apiGateway.validateRBACPermissionForAction,
        { group_guid: groupGuid, incident_id: incidentId }
      );
      return resp.data;
    },
    enabled: !!groupGuid && !!incidentId,
  });
};
